import { Play } from "lucide-react";
import { getStreamIframeUrl, getStreamThumbnailUrl } from "@/lib/media.utils";

/** Elemento del carrusel de la ficha pública: foto o vídeo. */
export type GalleryItem =
  | { id: string; kind: "image"; url: string; titulo: string | null }
  | {
      id: string;
      kind: "video";
      /** URL del iframe de Stream o del fichero en el bucket. */
      src: string;
      /** true si `src` es un iframe de Cloudflare Stream; false si es un fichero de vídeo. */
      stream: boolean;
      thumbnail: string | null;
      titulo: string | null;
    };

type PublicMedia = {
  id: string;
  tipo: "imagen" | "video";
  url: string | null;
  titulo: string | null;
  stream_video_id?: string | null;
  stream_status?: string | null;
};

/** Fotos y vídeos listos para reproducir, en el orden definido en el panel de media. */
export function buildGalleryItems(media: PublicMedia[]): GalleryItem[] {
  const out: GalleryItem[] = [];
  for (const m of media ?? []) {
    if (m.tipo === "imagen") {
      if (m.url) out.push({ id: m.id, kind: "image", url: m.url, titulo: m.titulo });
      continue;
    }
    if (m.stream_video_id) {
      if (m.stream_status !== "ready") continue;
      out.push({
        id: m.id,
        kind: "video",
        src: getStreamIframeUrl(m.stream_video_id),
        stream: true,
        thumbnail: getStreamThumbnailUrl(m.stream_video_id),
        titulo: m.titulo,
      });
    } else if (m.url) {
      out.push({
        id: m.id,
        kind: "video",
        src: m.url,
        stream: false,
        thumbnail: null,
        titulo: m.titulo,
      });
    }
  }
  return out;
}

/** Contenido principal de una diapositiva (imagen o reproductor de vídeo). */
export function GallerySlide({
  item,
  alt,
  imgClassName,
  videoClassName = "absolute inset-0 h-full w-full",
}: {
  item: GalleryItem;
  alt: string;
  imgClassName: string;
  videoClassName?: string;
}) {
  if (item.kind === "image") {
    return <img src={item.url} alt={alt} className={imgClassName} draggable={false} />;
  }
  if (item.stream) {
    return (
      <iframe
        key={item.id}
        src={item.src}
        title={alt}
        className={`${videoClassName} border-0 bg-black`}
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture;"
        allowFullScreen
        onClick={(e) => e.stopPropagation()}
      />
    );
  }
  return (
    <video
      key={item.id}
      src={item.src}
      controls
      playsInline
      preload="metadata"
      className={`${videoClassName} bg-black object-contain`}
      onClick={(e) => e.stopPropagation()}
    />
  );
}

/** Miniatura de la tira inferior; los vídeos llevan un icono de reproducción. */
export function GalleryThumb({ item, className }: { item: GalleryItem; className: string }) {
  const src = item.kind === "image" ? item.url : item.thumbnail;
  return (
    <>
      {src ? (
        <img src={src} alt="" className={className} draggable={false} />
      ) : (
        <div className="h-full w-full bg-black" />
      )}
      {item.kind === "video" && (
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="rounded-full bg-black/60 p-1.5 text-white">
            <Play className="h-3.5 w-3.5 fill-current" />
          </span>
        </span>
      )}
    </>
  );
}
